import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    LinearProgress,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
    Alert,
    Paper
} from '@mui/material';
import {
    CheckCircle,
    Description,
    People,
    Storage,
    CalendarToday,
    Upgrade,
    Person
} from '@mui/icons-material';
import axios from 'axios';

const getBackendURL = () => {
    // Prioridad 1: variable de entorno (VITE_API_URL incluye /api al final, la quitamos)
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL.replace(/\/api$/, '');
    }
    const hostname = window.location.hostname;
    const isPrivate = hostname === 'localhost' || hostname === '127.0.0.1' ||
        /^192\.168\./.test(hostname) || /^10\./.test(hostname) ||
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./.test(hostname);
    const port = import.meta.env.VITE_BACKEND_PORT || '63000';
    return isPrivate
        ? `http://${hostname}:${port}`
        : `${window.location.protocol}//${window.location.hostname}`;
};

const API_BASE_URL = getBackendURL();

export const AccountPage = () => {
    const [accountData, setAccountData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const fetchAccountData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('authToken');
            
            const response = await axios.get(`${API_BASE_URL}/api/account/usage`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setAccountData(response.data);
            } else {
                setError('Error al cargar información de la cuenta');
            }
        } catch (err) {
            console.error('[Account] Error fetching account data:', err);
            if (err.response?.status === 401) {
                setError('Sesión expirada. Por favor inicia sesión nuevamente.');
                setTimeout(() => navigate('/login'), 2000);
            } else {
                setError('No se pudo cargar la información de tu cuenta.');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccountData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            active: 'success',
            trial: 'info',
            expired: 'error',
            suspended: 'warning',
            cancelled: 'default'
        };
        return colors[status] || 'default';
    };

    const getStatusLabel = (status) => {
        const labels = {
            active: 'Activa',
            trial: 'Prueba',
            expired: 'Expirada',
            suspended: 'Suspendida',
            cancelled: 'Cancelada'
        };
        return labels[status] || status;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
                <CircularProgress size={60} />
            </Box>
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Alert severity="error">{error}</Alert>
            </Container>
        );
    }

    if (!accountData) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Alert severity="warning">No se encontró información de la cuenta</Alert>
            </Container>
        );
    }

    const { user, subscription, plan, usage } = accountData;

    return (
        <Container maxWidth="lg" sx={{ py: 4, mt: 2 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Mi Cuenta
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Gestiona tu suscripción y revisa tu uso actual
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Info del Usuario */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Person sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" fontWeight="600">
                                    Información Personal
                                </Typography>
                            </Box>
                            
                            <List dense>
                                <ListItem disablePadding sx={{ mb: 1 }}>
                                    <ListItemText 
                                        primary="Nombre"
                                        secondary={`${user.firstName} ${user.lastName}`}
                                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                    />
                                </ListItem>
                                
                                <ListItem disablePadding sx={{ mb: 1 }}>
                                    <ListItemText 
                                        primary="Email"
                                        secondary={user.email}
                                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                    />
                                </ListItem>

                                <ListItem disablePadding sx={{ mb: 1 }}>
                                    <ListItemText 
                                        primary="Estado de Email"
                                        secondary={user.emailVerified ? '✅ Verificado' : '❌ No verificado'}
                                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                    />
                                </ListItem>

                                <ListItem disablePadding sx={{ mb: 1 }}>
                                    <ListItemText 
                                        primary="Miembro desde"
                                        secondary={formatDate(user.memberSince)}
                                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                    />
                                </ListItem>
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Plan Actual */}
                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                <Box>
                                    <Typography variant="h6" fontWeight="600" gutterBottom>
                                        Plan Actual: {plan.name}
                                    </Typography>
                                    <Chip 
                                        label={getStatusLabel(subscription.status)} 
                                        color={getStatusColor(subscription.status)}
                                        size="small"
                                    />
                                </Box>
                                <Button 
                                    variant="contained" 
                                    startIcon={<Upgrade />}
                                    onClick={() => navigate('/plans')}
                                >
                                    Cambiar Plan
                                </Button>
                            </Box>

                            <Divider sx={{ mb: 3 }} />

                            {/* Características del Plan */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="body2" fontWeight="600" gutterBottom>
                                    Características incluidas:
                                </Typography>
                                <List dense>
                                    {(Array.isArray(plan.features) 
                                        ? plan.features.slice(0, 3)
                                        : Object.values(plan.features || {}).filter(f => typeof f === 'string').slice(0, 3)
                                    ).map((feature, idx) => (
                                        <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 28 }}>
                                                <CheckCircle color="success" fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={feature}
                                                primaryTypographyProps={{ fontSize: '0.85rem' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>

                            {/* Período de Facturación */}
                            <Paper elevation={0} sx={{ p: 2, bgcolor: 'action.hover' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <CalendarToday sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                                    <Typography variant="body2" fontWeight="600">
                                        Período de Facturación
                                    </Typography>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                                </Typography>
                                {subscription.daysUntilReset !== null && (
                                    <Typography variant="body2" color="primary" fontWeight="500" sx={{ mt: 1 }}>
                                        Se renueva en {subscription.daysUntilReset} día{subscription.daysUntilReset !== 1 ? 's' : ''}
                                    </Typography>
                                )}
                            </Paper>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Uso de Documentos */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Description sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" fontWeight="600">
                                    Documentos
                                </Typography>
                            </Box>

                            {usage.documents.unlimited ? (
                                <Box sx={{ textAlign: 'center', py: 3 }}>
                                    <Typography variant="h3" fontWeight="bold" color="primary">
                                        ∞
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Ilimitados
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                        {usage.documents.used} usados este mes
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="text.secondary">
                                            Usados
                                        </Typography>
                                        <Typography variant="body2" fontWeight="600">
                                            {usage.documents.used} / {usage.documents.limit}
                                        </Typography>
                                    </Box>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={usage.documents.percentage} 
                                        sx={{ 
                                            height: 8, 
                                            borderRadius: 4,
                                            mb: 1,
                                            bgcolor: 'action.hover'
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {usage.documents.remaining} documentos restantes
                                    </Typography>
                                </>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Uso de Almacenamiento */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Storage sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" fontWeight="600">
                                    Almacenamiento
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Usado
                                </Typography>
                                <Typography variant="body2" fontWeight="600">
                                    {usage.storage.usedGB} GB / {usage.storage.limitGB} GB
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={usage.storage.percentage} 
                                sx={{ 
                                    height: 8, 
                                    borderRadius: 4,
                                    mb: 1,
                                    bgcolor: 'action.hover'
                                }}
                                color={usage.storage.percentage > 90 ? 'error' : 'primary'}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {(usage.storage.remainingMB / 1024).toFixed(2)} GB disponibles
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Firmantes */}
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <People sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="h6" fontWeight="600">
                                    Firmantes
                                </Typography>
                            </Box>

                            <Box sx={{ textAlign: 'center', py: 3 }}>
                                {usage.signers.unlimited ? (
                                    <>
                                        <Typography variant="h3" fontWeight="bold" color="primary">
                                            ∞
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Ilimitados por documento
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <Typography variant="h3" fontWeight="bold" color="primary">
                                            {usage.signers.limit}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Por documento
                                        </Typography>
                                    </>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
};

export default AccountPage;
